
import { defineComponent, reactive, computed, PropType, watch, ref } from "vue";
import InputDate from "@/components/InputDate.vue";
import ErrorMessage from "@/components/ErrorMessage.vue";
import { fromUnixTime, toUnixTime } from "@/lib/shared";
import store from "@/stores";
import {
  ContractStatus,
  ContractType,
  ErrorMessageFilter,
  IContract,
  IErrorResponse,
  isErrors,
  consts,
} from "@/services/Models";
import { AxiosResponse } from "axios";

export enum EditMode {
  Edit,
  Stop,
  Restart,
  Delete,
}

export default defineComponent({
  name: "AdminContractEdit",
  components: {
    InputDate,
    ErrorMessage,
  },
  props: {
    isShowModal: { type: Boolean },
    editMode: { type: Number },
    editModel: { type: Object as PropType<IContract> },
  },
  setup(props, context) {
    const isShowModalInner = computed({
      get: () => props.isShowModal,
      set: (newValue) => {
        context.emit("update:isShowModal", newValue);
      },
    });
    const errors = ref<IErrorResponse[]>([]);
    const errorMessages = computed(() => ({
      companyName: ErrorMessageFilter(errors.value, "CompanyName"),
      optionName: ErrorMessageFilter(errors.value, "OptionName"),
      expire: ErrorMessageFilter(errors.value, "Expire"),
    }));

    const title = computed(() => {
      switch (props.editMode) {
        case EditMode.Stop:
          return consts.editModeStopLabel;
        case EditMode.Restart:
          return consts.editModeRestartLabel;
        case EditMode.Delete:
          return consts.editModeDeleteLabel;
        case EditMode.Edit:
        default:
          return consts.editModeEditLabel;
      }
    });
    const buttonName = computed(() => {
      switch (props.editMode) {
        case EditMode.Stop:
          return consts.editModeStopLabel;
        case EditMode.Restart:
          return consts.editModeRestartLabel;
        case EditMode.Delete:
          return consts.editModeDeleteLabel;
        case EditMode.Edit:
        default:
          return consts.editModeApplyLabel;
      }
    });
    const enterAction = async () => {
      switch (props.editMode) {
        case EditMode.Stop:
          disable();
          break;
        case EditMode.Restart:
          enable();
          break;
        case EditMode.Delete:
          remove();
          break;
        case EditMode.Edit:
        default:
          apply();
          break;
      }
    };
    const contract = reactive({
      companyName: "",
      optionName: "",
      plan: 0,
      signed: new Date(),
      expire: new Date(),
      describe: "",
      type: ContractType.Trial,
    });
    const update = () => {
      if (!props.editModel) return;
      if (!props.isShowModal) return;
      contract.companyName = props.editModel.companyName;
      contract.optionName = props.editModel.optionName;
      contract.plan = props.editModel.plan;
      contract.signed = fromUnixTime(props.editModel.signed);
      contract.expire = fromUnixTime(props.editModel.expire);
      contract.describe = props.editModel.describe;
      contract.type = props.editModel.type;
    };
    // 対象の契約をウォッチして変更があれば契約内容を更新する
    watch(() => props.editModel, update);
    watch(() => props.isShowModal, update);
    watch(
      () => isShowModalInner.value,
      (newVal) => {
        if (!newVal) {
          errors.value = [];
        }
      }
    );

    const resource = store.useResource();
    // 契約情報を受け取り、その内容でDBを更新する
    const applyInner = async (f: (_: IContract) => IContract) => {
      const model = f({
        id: props.editModel?.id,
        companyName: contract.companyName,
        optionName: contract.optionName,
        plan: contract.plan,
        signed: toUnixTime(contract.signed),
        expire: toUnixTime(contract.expire),
        describe: contract.describe,
        users: props.editModel?.users ?? [],
        status: props.editModel?.status ?? ContractStatus.Enabled,
        type: contract.type,
      });
      // context.emit("apply", model);
      await resource
        .saveContract(model)
        .then(() => {
          isShowModalInner.value = false;
          errors.value = [];
        })
        .catch((ex: AxiosResponse) => {
          if (isErrors(ex.data)) {
            errors.value = ex.data as IErrorResponse[];
          } else {
            alert(consts.updateContractFailure);
          }
        });
    };
    const apply = async () => {
      applyInner((x) => x);
    };
    const enable = async () => {
      applyInner((x) => {
        x.status = ContractStatus.Enabled;
        return x;
      });
    };
    const disable = async () => {
      applyInner((x) => {
        x.status = ContractStatus.Disabled;
        return x;
      });
    };
    const remove = async () => {
      context.emit("remove", props.editModel);
      isShowModalInner.value = false;
    };

    const isEditable = computed(() => props.editMode == EditMode.Edit);
    const isDeleteMode = computed(() => props.editMode == EditMode.Delete);
    return {
      isShowModalInner,
      isEditable,
      isDeleteMode,
      title,
      buttonName,
      contract,
      apply,
      enable,
      disable,
      remove,
      enterAction,
      errorMessages,
      consts,
    };
  },
});
